import { Injectable } from '@angular/core';
import { grpc } from '@improbable-eng/grpc-web';
import { GrpcSimulationService } from '../generated/simulation_pb_service';
import { CanvasService } from './canvas.service';
import { LoopReqeust, LoopResponse } from '../generated/simulation_pb';
import { SimulationStep } from '../generated/models_pb';
import * as THREE from 'three';
import { SimulationService } from './simulation.service';
import { SimulationDataState, SimulationState } from '../enums/simulation-state';
import { Store } from '@ngxs/store';
import { Severity } from 'src/app/global/enums/severity';
import { NotificationsActions } from 'src/app/state/notifications/notifications.actions';
import { RequestHeaderService } from './request-header.service';
import { InactivityService } from './inactivity.service';
import { BehaviorSubject, Observable, Subject, concatMap, interval, map, take, takeUntil } from 'rxjs';
import { LoopSimulationStatus as ILoopSimulationStatus } from '../interfaces/loop-simulation-status';
import { LoopSimulationStatus } from './loop-simulation-status';
import { ApiTerrainService } from './api-terrain.service';

@Injectable({
  providedIn: 'root'
})
export class SimulationLoopService {
  /** the current simulation iteration status */
  private simulationStatus: ILoopSimulationStatus = new LoopSimulationStatus (0, 0, 0);
  private simulationStatusSubject = new BehaviorSubject<ILoopSimulationStatus>(new LoopSimulationStatus(0, 0, 0));

  /**simulation status update */
  public simulationStatus$: Observable<ILoopSimulationStatus> = this.simulationStatusSubject.asObservable();

  updateSimulationStatus(loopResponseObject: LoopResponse.AsObject ): void {
    this.simulationStatus.updateStats(loopResponseObject.totalunique, loopResponseObject.totalspawned, loopResponseObject.startingagents, loopResponseObject.landslidecount);
    this.simulationStatusSubject.next(this.simulationStatus);
  }

  resetSimulationStatus(): void {
    this.simulationStatus = new LoopSimulationStatus(0, 0, 0);
    this.simulationStatusSubject.next(this.simulationStatus);
  }

  getSimulationStatus(): ILoopSimulationStatus {
    return this.simulationStatus;
  }

  private readonly messages: { [index: string]: string; } = {
    loopSimulationCompleted: 'Loop Simulation completed successfully.',
    loopSimulationFailed: 'Loop Simulation failed during execution.',
    loopSimulationKilled: 'Loop Simulation killed successfully.',
    loopSimulationKillFail: 'Loop Simulation kill failed.',
  };

  private grpcLoopClient!: { close: () => void; };
  private currentLoopIndex: number = 0;

  constructor(private canvasService: CanvasService,
    private requestHeaderService: RequestHeaderService,
    private apiTerrainService: ApiTerrainService,
    private simulationService: SimulationService,
    private store: Store,
    private inactivityService: InactivityService) {
  }

  public isLooping(): boolean {
    return this.simulationService.simulationState === SimulationState.InLoop;
  }

  private setSimulationState(state: SimulationState) {
    this.simulationService.changeSimulationState(state)
  }

  lastSimulationId: string = '';
  public loop(simulationId: string, repetitionRate: number, loopVisualizationsDisabled: boolean, loopCompleteCallback?: () => void) {
    if (this.isLooping()) {
      this.killLoop(this.lastSimulationId);
      return;
    }

    this.lastSimulationId = simulationId;

    this.setSimulationState(SimulationState.InLoop);
    this.getLoopData(simulationId, repetitionRate, loopVisualizationsDisabled, () => {
      if (loopCompleteCallback) loopCompleteCallback();
    });
  }

  public killLastSimulationLoop() {
    if (this.isLooping()) {
      this.killLoop(this.lastSimulationId);
    }
  }

  public killLoop(simulationId: string) {
    this.setSimulationState(SimulationState.Completed);
    this.clearLoopResponses();
    this.grpcLoopClient.close();
    this.apiTerrainService.killLoop(simulationId).subscribe({
      next: () => {
        console.log(this.messages['loopSimulationKilled']);
      },
      error: (err) => {
        console.error(this.messages['loopSimulationKillFail'], err);
      }
    });
  }

  public reset() {
    this.currentLoopIndex = 0;
    this.simulationService.changeSimulationState(SimulationState.Ready)
    this.clearLoopResponses();
    this.resetSimulationStatus();
    if (this.latestLoopStep) {
      this.latestLoopStep.length = 0;
    }
  }

  loopResponses$: Subject<{id: number, response: LoopResponse, iterations: number}> = new Subject<{id: number, response: LoopResponse, iterations: number}>();
  private destroy$ = new Subject<void>();


  /** listen and react to simulation result data */
  listenToSimulationResults(loopEndedCallback?: () => void, throttle: number = 5){
    this.getThrottledLoopResponses(throttle).subscribe((loopResponse) => {
      const loopResponseObject: LoopResponse.AsObject = loopResponse.response.toObject();

      // update our global simulation status
      this.updateSimulationStatus(loopResponseObject);

      this.setSimulationState(SimulationState.InLoop);
      this.paintLoopResult(loopResponseObject.stepsList);

      if (loopResponse.id == loopResponse.iterations) {
        if(loopEndedCallback) loopEndedCallback();
        this.setSimulationState(SimulationState.Completed);
        this.store.dispatch(new NotificationsActions.Toast(this.messages['loopSimulationCompleted'], Severity.Success));
      }
    });
  }

  /** trickle the simulation responses */
  getThrottledLoopResponses(throttle: number, killSubject?: Subject<void>) {
    return this.loopResponses$.pipe(
      concatMap(loopResponse =>
        interval(throttle).pipe(
          take(1),  // Only emit once after throttle time
          map(() => loopResponse)
        )
      ),
      takeUntil(killSubject ? killSubject : this.destroy$)
    );
  }

  private clearLoopResponses() {
    this.destroy$.next();  // Emit a value to close the LoopResponses$ Subject
    this.destroy$.complete();  // Complete the destroy$ Subject
    this.loopResponses$ = new Subject<{id: number, response: LoopResponse, iterations: number}>();  // Create a new loop response Subject
    this.destroy$ = new Subject<void>();  // Create a new destroy$ Subject for future use
  }

  private getLoopData(simulationId: string, repetitionRate: number, loopVisualizationsDisabled: boolean, loopEndedCallback: () => void) {
    this.clearLoopResponses();
    this.listenToSimulationResults(loopEndedCallback, 1);
    const request: LoopReqeust = new LoopReqeust();
    request.setId(simulationId);
    request.setRepetitionrate(repetitionRate);
    request.setLoopvisualizationsdisabled(loopVisualizationsDisabled);
    request.setSavetrackerresults(false);
    this.currentLoopIndex = 0;

    this.simulationStatus = new LoopSimulationStatus(repetitionRate, 0, 0);
    // placeholder until loop logic is completed. Shows kill button
    this.requestHeaderService.getHeaderPromise().then((metadata) => {
      this.inactivityService.resetInactivityTimer();
      this.grpcLoopClient = grpc.invoke(GrpcSimulationService.Loop, {
        request: request,
        host: '',
        metadata: new grpc.Metadata(metadata),
        onMessage: async (message: LoopResponse) => {
          this.inactivityService.resetInactivityTimer();
          this.simulationService.simulationDataState = SimulationDataState.Receiving;
          this.loopResponses$.next({id: this.currentLoopIndex++,  response: message, iterations: repetitionRate});
        },
        onEnd: (
          code: grpc.Code,
          message: string,
          trailers: grpc.Metadata
        ) => {
          if (code !== grpc.Code.OK) {
            console.error('gRPC Simulate: An error occured', code, message, trailers);
            this.simulationService.simulationDataState = SimulationDataState.Failed;
            this.store.dispatch(new NotificationsActions.Toast(this.messages['loopSimulationFailed'], Severity.Error));
            return;
          }

          this.simulationService.simulationDataState = SimulationDataState.Received;
          //success handling is done at the end of the loopResponses loop
        }
      });
    })
  }

  private async paintLoopResult(steps: SimulationStep.AsObject[]) {
    this.eraseLatestLoopStep();
    this.paintLoopStep(steps);
    this.latestLoopStep = steps;
  }

  private latestLoopStep?: SimulationStep.AsObject[];

  private eraseLatestLoopStep() {
    if (!this.latestLoopStep) {
      return;
    }
    this.canvasService.removeSimulationPaints(
      this.latestLoopStep.map((c) => c.index)
    );
  }

  private paintLoopStep(steps: SimulationStep.AsObject[]) {
    steps.forEach((step) => {

      if (step.color) {
        this.canvasService.paintCanvas(
          step.index,
          new THREE.Color(step.color.r, step.color.g, step.color.b)
        );
      }
    });
    this.canvasService.updateTexture();
    this.canvasService.animate();
  }
}
